import { useState } from "react";
import { Check, Eye, EyeOff } from "lucide-react";
import { Button, ErrorAlert, Modal } from "../../components/ui";
import { mensagemDeErro } from "../chamados/gestao/api";
import { useTrocarSenha } from "./api";
import { problemaDaSenhaNova, SENHA_MAXIMO } from "./regrasDeSenha";

/**
 * Troca de senha do próprio usuário. As regras são as mesmas que o servidor
 * cobra (atual obrigatória; nova com ao menos seis caracteres, sem acento e
 * diferente da atual), conferidas aqui para a pessoa saber antes de enviar. A confirmação
 * é só da tela: serve contra o erro de digitação, que ninguém descobriria até
 * o próximo login.
 */
export const TrocarSenhaDialog = ({ aoFechar }: { aoFechar: () => void }) => {
  const trocar = useTrocarSenha();

  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [mostrar, setMostrar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);

  const problema =
    atual.length === 0
      ? "Informe a senha atual."
      : (problemaDaSenhaNova(nova) ??
        (nova === atual
          ? "A nova senha precisa ser diferente da atual."
          : confirmacao !== nova
            ? "A confirmação não confere com a nova senha."
            : null));

  // Só cobra o campo depois de a pessoa mexer nele: um formulário que abre
  // vermelho não ajuda ninguém.
  const mostrarProblema = confirmacao.length > 0 || nova.length > 0;

  const enviar = async () => {
    setErro(null);
    if (problema) return;

    try {
      await trocar.mutateAsync({ senhaAtual: atual, novaSenha: nova });
      setPronto(true);
    } catch (e) {
      setErro(mensagemDeErro(e, "Não foi possível trocar a senha."));
    }
  };

  if (pronto) {
    return (
      <Modal
        isOpen
        onClose={aoFechar}
        title="Senha trocada"
        size="sm"
        footer={
          <div className="flex justify-end">
            <Button onClick={aoFechar}>Fechar</Button>
          </div>
        }
      >
        <p className="flex items-start gap-2 text-sm text-gray-700">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          A nova senha já vale. Use ela no próximo acesso; esta sessão continua
          aberta.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={aoFechar}
      title="Trocar senha"
      size="sm"
      closeOnOverlayClick={!trocar.isPending}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={aoFechar} disabled={trocar.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={enviar}
            loading={trocar.isPending}
            disabled={Boolean(problema)}
            className="bg-gradient-to-r from-blue-800 to-blue-600 text-white hover:from-blue-900 hover:to-blue-700"
          >
            {trocar.isPending ? "Trocando…" : "Trocar senha"}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Campo
          id="senha-atual"
          rotulo="Senha atual"
          valor={atual}
          aoMudar={setAtual}
          mostrar={mostrar}
          aoAlternar={() => setMostrar((v) => !v)}
          autoFocus
        />
        <Campo
          id="senha-nova"
          rotulo="Nova senha"
          valor={nova}
          aoMudar={setNova}
          mostrar={mostrar}
        />
        <Campo
          id="senha-confirmacao"
          rotulo="Repita a nova senha"
          valor={confirmacao}
          aoMudar={setConfirmacao}
          mostrar={mostrar}
        />

        {problema && mostrarProblema && (
          <p className="text-sm text-amber-700">{problema}</p>
        )}

        {erro && (
          <ErrorAlert
            title="Não foi possível trocar a senha"
            message={erro}
            onClose={() => setErro(null)}
            variant="error"
          />
        )}
      </div>
    </Modal>
  );
};

interface CampoProps {
  id: string;
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  mostrar: boolean;
  aoAlternar?: () => void;
  autoFocus?: boolean;
}

const Campo = ({
  id,
  rotulo,
  valor,
  aoMudar,
  mostrar,
  aoAlternar,
  autoFocus,
}: CampoProps) => (
  <div>
    <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">
      {rotulo}
    </label>
    <div className="relative">
      <input
        id={id}
        type={mostrar ? "text" : "password"}
        value={valor}
        autoFocus={autoFocus}
        autoComplete={id === "senha-atual" ? "current-password" : "new-password"}
        onChange={(e) => aoMudar(e.target.value)}
        maxLength={SENHA_MAXIMO}
        className="h-10 w-full rounded-lg border border-gray-300 px-3 pr-10 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
      />
      {/* O olho fica só no primeiro campo e vale para os três: um alternador por
          campo triplicaria o controle sem dar nada em troca. */}
      {aoAlternar && (
        <button
          type="button"
          onClick={aoAlternar}
          aria-label={mostrar ? "Ocultar senhas" : "Mostrar senhas"}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition-colors hover:text-gray-600"
        >
          {mostrar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </div>
  </div>
);

export default TrocarSenhaDialog;
